import { useCallback, useMemo, useState } from "react";
import type { Match } from "../types";
import { IS_E2E_TEST_MODE } from "../../../lib/loggerApi";
import {
  QUARTER_SECONDS,
  OVERTIME_SECONDS,
  type PeriodPhase,
} from "./usePeriodManager";
import { formatSecondsAsClock } from "../lib/clockHelpers";

interface UseTransitionGuardsParams {
  statusOverride?: Match["status"];
  currentPhase: PeriodPhase;
  globalTimeSeconds: number;
  match: Match | null;
  t: (...args: any[]) => any;
}

export interface UseTransitionGuardsResult {
  currentStatusNormalized: Match["status"];
  canBreakQ1: boolean;
  canQ2: boolean;
  canHalftime: boolean;
  canQ3: boolean;
  canBreakQ3: boolean;
  canQ4: boolean;
  canFulltime: boolean;
  transitionGuardMessage: string;
  transitionError: string | null;
  hasQ1Minimum: boolean;
  hasQ2Minimum: boolean;
  hasQ3Minimum: boolean;
  hasQ4Minimum: boolean;
  hasOTMinimum: boolean;
  minimumQ1Reason: string;
  minimumQ2Reason: string;
  minimumQ3Reason: string;
  minimumQ4Reason: string;
  minimumOTReason: string;
  guardTransition: (target: Match["status"], fn?: () => void) => void;
  isTransitionAllowed: (
    target: Match["status"],
    currentOverride?: Match["status"],
  ) => boolean;
}

export const useTransitionGuards = ({
  statusOverride,
  currentPhase,
  globalTimeSeconds,
  match,
  t,
}: UseTransitionGuardsParams): UseTransitionGuardsResult => {
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const normalizeStatus = useCallback(
    (status?: Match["status"]): Match["status"] => {
      if (status === "Live") return "Live_Q1";
      return status || "Pending";
    },
    [],
  );

  const isTransitionAllowed = useCallback(
    (target: Match["status"], currentOverride?: Match["status"]): boolean => {
      const current = normalizeStatus(currentOverride ?? statusOverride);
      const allowed: Record<Match["status"], Match["status"][]> = {
        Pending: ["Live_Q1"],
        Live_Q1: ["Break_Q1"],
        Break_Q1: ["Live_Q2"],
        Live_Q2: ["Halftime"],
        Halftime: ["Live_Q3"],
        Live_Q3: ["Break_Q3"],
        Break_Q3: ["Live_Q4"],
        Live_Q4: ["Fulltime"],
        Fulltime: ["Live_OT", "Completed"],
        Live_OT: ["Break_OT", "Completed"],
        Break_OT: ["Live_OT", "Completed"],
        Live: ["Break_Q1"],
        Completed: [],
        Abandoned: [],
        Scheduled: ["Live_Q1"],
      };
      const allowedTargets = allowed[current] || [];
      return allowedTargets.includes(target);
    },
    [normalizeStatus, statusOverride],
  );

  const bypassMinimums =
    IS_E2E_TEST_MODE &&
    !(
      typeof window !== "undefined" &&
      (window as any).__BASKET_PROMATCH_E2E_ENFORCE_MINIMUMS__
    );

  const getPeriodStartSeconds = useCallback(
    (period: number) => {
      if (period === 1) return 0;

      const canonicalStart = (period - 1) * QUARTER_SECONDS;

      const raw =
        match?.period_timestamps?.[String(period)]?.global_start_seconds;
      if (
        typeof raw === "number" &&
        Number.isFinite(raw) &&
        raw >= 0 &&
        raw <= globalTimeSeconds
      ) {
        return raw;
      }

      return canonicalStart;
    },
    [globalTimeSeconds, match],
  );

  const q1Elapsed = Math.max(0, globalTimeSeconds - getPeriodStartSeconds(1));
  const q2Elapsed = Math.max(0, globalTimeSeconds - getPeriodStartSeconds(2));
  const q3Elapsed = Math.max(0, globalTimeSeconds - getPeriodStartSeconds(3));
  const q4Elapsed = Math.max(0, globalTimeSeconds - getPeriodStartSeconds(4));
  const otElapsed = Math.max(0, globalTimeSeconds - getPeriodStartSeconds(5));

  const hasQ1Minimum = bypassMinimums || q1Elapsed >= QUARTER_SECONDS;
  const hasQ2Minimum = bypassMinimums || q2Elapsed >= QUARTER_SECONDS;
  const hasQ3Minimum = bypassMinimums || q3Elapsed >= QUARTER_SECONDS;
  const hasQ4Minimum = bypassMinimums || q4Elapsed >= QUARTER_SECONDS;
  const hasOTMinimum = bypassMinimums || otElapsed >= OVERTIME_SECONDS;

  const minimumQ1Reason = t(
    "transitionMinimumQ1",
    "Need at least 10:00 of global time from Q1 start (current {{clock}}).",
    { clock: formatSecondsAsClock(q1Elapsed) },
  );
  const minimumQ2Reason = t(
    "transitionMinimumQ2",
    "Need at least 10:00 from Q2 start (current {{clock}}).",
    { clock: formatSecondsAsClock(q2Elapsed) },
  );
  const minimumQ3Reason = t(
    "transitionMinimumQ3",
    "Need at least 10:00 from Q3 start (current {{clock}}).",
    { clock: formatSecondsAsClock(q3Elapsed) },
  );
  const minimumQ4Reason = t(
    "transitionMinimumQ4",
    "Need at least 10:00 from Q4 start (current {{clock}}).",
    { clock: formatSecondsAsClock(q4Elapsed) },
  );
  const minimumOTReason = t(
    "transitionMinimumOT",
    "Need at least 05:00 from OT start (current {{clock}}).",
    { clock: formatSecondsAsClock(otElapsed) },
  );

  const guardTransition = useCallback(
    (target: Match["status"], fn?: () => void) => {
      if (!fn) return;
      const current = normalizeStatus(statusOverride);
      const phaseToStatus: Record<string, Match["status"]> = {
        NOT_STARTED: "Pending",
        Q1: "Live_Q1",
        BREAK_Q1: "Break_Q1",
        Q2: "Live_Q2",
        HALFTIME: "Halftime",
        Q3: "Live_Q3",
        BREAK_Q3: "Break_Q3",
        Q4: "Live_Q4",
        FULLTIME: "Fulltime",
        OVERTIME: "Live_OT",
        BREAK_OT: "Break_OT",
        COMPLETED: "Completed",
      };
      const allowedStatus = phaseToStatus[currentPhase] ?? current;

      // Minimum time guards per quarter
      if (target === "Break_Q1" && currentPhase === "Q1" && !hasQ1Minimum) {
        setTransitionError(minimumQ1Reason);
        return;
      }
      if (target === "Halftime" && currentPhase === "Q2" && !hasQ2Minimum) {
        setTransitionError(minimumQ2Reason);
        return;
      }
      if (target === "Break_Q3" && currentPhase === "Q3" && !hasQ3Minimum) {
        setTransitionError(minimumQ3Reason);
        return;
      }
      if (target === "Fulltime" && currentPhase === "Q4" && !hasQ4Minimum) {
        setTransitionError(minimumQ4Reason);
        return;
      }
      if (
        (target === "Break_OT" || target === "Completed") &&
        currentPhase === "OVERTIME" &&
        !hasOTMinimum
      ) {
        setTransitionError(minimumOTReason);
        return;
      }

      if (!isTransitionAllowed(target, allowedStatus)) {
        setTransitionError(
          `Transition not allowed: ${current} → ${target}. Follow Pending → Live_Q1 → Break_Q1 → Live_Q2 → Halftime → Live_Q3 → Break_Q3 → Live_Q4 → Fulltime.`,
        );
        return;
      }

      setTransitionError(null);
      fn();
    },
    [
      currentPhase,
      hasQ1Minimum,
      hasQ2Minimum,
      hasQ3Minimum,
      hasQ4Minimum,
      hasOTMinimum,
      isTransitionAllowed,
      minimumQ1Reason,
      minimumQ2Reason,
      minimumQ3Reason,
      minimumQ4Reason,
      minimumOTReason,
      normalizeStatus,
      statusOverride,
    ],
  );

  const currentStatusNormalized = normalizeStatus(statusOverride);
  const canBreakQ1 = isTransitionAllowed("Break_Q1");
  const canQ2 = isTransitionAllowed("Live_Q2");
  const canHalftime = isTransitionAllowed("Halftime");
  const canQ3 = isTransitionAllowed("Live_Q3");
  const canBreakQ3 = isTransitionAllowed("Break_Q3");
  const canQ4 = isTransitionAllowed("Live_Q4");
  const canFulltime = isTransitionAllowed("Fulltime");

  const transitionGuardMessage = useMemo(
    () =>
      t(
        "transitionGuardMessage",
        "Follow order: Pending → Live_Q1 → Break_Q1 → Live_Q2 → Halftime → Live_Q3 → Break_Q3 → Live_Q4 → Fulltime (current: {{status}}).",
        { status: currentStatusNormalized },
      ),
    [currentStatusNormalized, t],
  );

  return {
    currentStatusNormalized,
    canBreakQ1,
    canQ2,
    canHalftime,
    canQ3,
    canBreakQ3,
    canQ4,
    canFulltime,
    transitionGuardMessage,
    transitionError,
    hasQ1Minimum,
    hasQ2Minimum,
    hasQ3Minimum,
    hasQ4Minimum,
    hasOTMinimum,
    minimumQ1Reason,
    minimumQ2Reason,
    minimumQ3Reason,
    minimumQ4Reason,
    minimumOTReason,
    guardTransition,
    isTransitionAllowed,
  };
};
